import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useApi } from '@/utils/axios'
import { useNotificationStore } from './notification'
import { MENSAGEM_ROTAS, WHATSAPP_ROTAS } from '@/utils/apiRoutes'

export const useBoletoStore = defineStore('boletos', () => {
  // State
  const loading = ref(false)
  const selectedPeriod = ref('todos')
  const whatsappTemplates = ref([])
  const selectedWhatsappTemplate = ref(null)
  const availableParameters = ref({})
  const selectedParameters = ref([])
  
  // Notification store
  const notificationStore = useNotificationStore()
  
  // Buscar templates do WhatsApp
  const buscarTemplatesWhatsapp = async (provedor = 'ummsg') => {
    loading.value = true
    try {
      const response = await useApi(WHATSAPP_ROTAS.TEMPLATES).fetch({ params: { provedor } });
      
      if (response?.status === 'success' && Array.isArray(response.data)) {
        whatsappTemplates.value = response.data;
        
        // Se tiver templates, seleciona o primeiro por padrão
        if (whatsappTemplates.value.length > 0) {
          selectedWhatsappTemplate.value = whatsappTemplates.value[0].code;
        }
        
        return response.data;
      } else {
        console.error('Erro ao buscar templates do WhatsApp:', response);
        throw new Error(response?.message || 'Erro ao buscar templates do WhatsApp');
      }
    } catch (error) {
      console.error('Erro ao buscar templates do WhatsApp:', error);
      notificationStore.showNotification('Erro ao buscar templates do WhatsApp', 'error');
      return [];
    } finally {
      loading.value = false;
    }
  };

  // Buscar parâmetros disponíveis
  const buscarParametrosDisponiveis = async (categoria = 'COBRANCA_POS') => {
    loading.value = true
    try {
      const response = await useApi(WHATSAPP_ROTAS.PARAMETROS).fetch({ params: { categoria } });
      
      if (response?.status === 'success' && response.data) {
        availableParameters.value = response.data.parametros || {};
        selectedParameters.value = [];
        
        return response.data;
      } else {
        console.error('Erro ao buscar parâmetros:', response);
        throw new Error(response?.message || 'Erro ao buscar parâmetros');
      }
    } catch (error) {
      console.error('Erro ao buscar parâmetros:', error);
      notificationStore.showNotification('Erro ao buscar parâmetros', 'error');
      return {};
    } finally {
      loading.value = false;
    }
  };
  
  // Função para enviar boletos pós
  const enviarBoletos = async (periodo, api, phoneNumber = null, whatsappTemplateId = null, parametros = []) => {
    loading.value = true
    try {
      // Verificar se existe um template selecionado para ummsg
      if (api === 'ummsg' && !whatsappTemplateId) {
        throw new Error('Por favor, selecione um template do WhatsApp');
      }
      
      // Preparar os parâmetros para envio conforme especificação da API
      const payload = {
        periodo: periodo,
        provedor: api // 'need', 'utalk' ou 'ummsg'
      }

      // Adicionar parâmetros específicos para templates do WhatsApp (ummsg)
      if (api === 'ummsg' && whatsappTemplateId) {
        payload.template_id = whatsappTemplateId;
        payload.parametros = parametros || [];
        // Para ummsg não precisamos mais do mensagem_id dos templates antigos
      } else {
        // Para need e utalk, ainda usamos o sistema antigo por enquanto
        // mas pode ser removido quando migrarmos todos os provedores
        payload.mensagem_id = 1; // valor padrão ou pode ser removido completamente
      }

      // Adicionar número de telefone se for teste
      if (periodo === 'teste' && phoneNumber) {
        payload.telefone = phoneNumber;
      }

      console.log('Enviando boletos com parâmetros:', payload);

      try {
        // Enviar a requisição para a API
        const response = await useApi(MENSAGEM_ROTAS.ENVIAR_WHATSAPP_COBRANCA).post(payload);

        // Verificar se a requisição foi bem-sucedida
        if (response?.status !== 'success') {
          throw new Error(response?.message || 'Erro ao enviar boletos');
        }
        
        // Extrair informações da resposta
        const responseData = response.data;
        const totalRevendedores = responseData.total_revendedores || 0;
        
        notificationStore.showNotification(
          `${responseData.message || `${totalRevendedores} boletos enviados com sucesso via ${api === 'need' ? 'NeedConversas' : api === 'utalk' ? 'UTalk' : '1msg'}`}`, 
          'success'
        );
        
        return responseData;
      } catch (error) {
        // Verificar se é o erro específico de não encontrar revendedores
        if (error.response && error.response.status === 404) {
          const errorMessage = error.response.data?.message || 'Nenhum revendedor com cobrança em aberto encontrado.';
          
          notificationStore.showNotification(errorMessage, 'warning');
          
          // Retornar um objeto com as informações para exibição na tela
          return {
            message: errorMessage,
            total_revendedores: 0,
            status: 'warning'
          };
        }
        // Se não for o erro 404 específico, propagar o erro
        throw error;
      }
    } catch (error) {
      console.error('Erro ao enviar boletos:', error);
      notificationStore.showNotification(
        error.message || 'Erro ao enviar boletos', 
        'error'
      );
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Função para enviar boletos pré
  const enviarBoletosPre = async (creditoMaximo, api, whatsappTemplateId = null, parametros = []) => {
    loading.value = true
    try {
      // Verificar se existe um template selecionado para ummsg
      if (api === 'ummsg' && !whatsappTemplateId) {
        throw new Error('Por favor, selecione um template do WhatsApp');
      }
      
      // Preparar os parâmetros para envio conforme especificação da API
      const payload = {
        credito_maximo: creditoMaximo,
        provedor: api // 'need', 'utalk' ou 'ummsg'
      }

      // Adicionar parâmetros específicos para templates do WhatsApp (ummsg)
      if (api === 'ummsg' && whatsappTemplateId) {
        payload.template_id = whatsappTemplateId;
        payload.parametros = parametros || [];
      } else {
        // Para need e utalk, ainda usamos o sistema antigo por enquanto
        payload.mensagem_id = 1; // valor padrão ou pode ser removido completamente
      }

      console.log('Enviando boletos pré com parâmetros:', payload);

      try {
        // Enviar a requisição para a API
        const response = await useApi(MENSAGEM_ROTAS.ENVIAR_WHATSAPP_CREDITO_BAIXO).post(payload);

        // Verificar se a requisição foi bem-sucedida
        if (response?.status !== 'success') {
          throw new Error(response?.message || 'Erro ao enviar boletos');
        }
        
        // Extrair informações da resposta
        const responseData = response.data;
        
        notificationStore.showNotification(
          `${responseData.message || 'Processamento iniciado com sucesso'}`, 
          'success'
        );
        
        return responseData;
      } catch (error) {
        // Verificar se é o erro específico de não encontrar revendedores
        if (error.response && error.response.status === 404) {
          const errorMessage = error.response.data?.message || 'Nenhum revendedor com créditos abaixo do limite encontrado.';
          
          notificationStore.showNotification(errorMessage, 'warning');
          
          // Retornar um objeto com as informações para exibição na tela
          return {
            message: errorMessage,
            total_revendedores: 0,
            status: 'warning'
          };
        }
        // Se não for o erro 404 específico, propagar o erro
        throw error;
      }
    } catch (error) {
      console.error('Erro ao enviar boletos pré:', error);
      notificationStore.showNotification(
        error.message || 'Erro ao enviar boletos pré', 
        'error'
      );
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Função para alterar o período selecionado
  const setPeriod = (period) => {
    selectedPeriod.value = period
  }

  // Função para alterar o template do WhatsApp selecionado
  const setWhatsappTemplate = (templateCode) => {
    selectedWhatsappTemplate.value = templateCode
  }

  // Função para alterar os parâmetros selecionados
  const setSelectedParameters = (parameters) => {
    selectedParameters.value = parameters
  }

  return {
    // State
    loading,
    selectedPeriod,
    whatsappTemplates,
    selectedWhatsappTemplate,
    availableParameters,
    selectedParameters,
    
    // Actions
    enviarBoletos,
    enviarBoletosPre,
    buscarTemplatesWhatsapp,
    buscarParametrosDisponiveis,
    setPeriod,
    setWhatsappTemplate,
    setSelectedParameters
  }
})
